import type { ReactNode, Ref } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import Check from '@mui/icons-material/Check';

export interface ReportPickerCardProps {
  /** Leading glyph (a MUI icon element). */
  icon: ReactNode;
  title: string;
  description: string;
  selected: boolean;
  onSelect: () => void;
  ref?: Ref<HTMLButtonElement>;
}

/**
 * Selectable report card used by the Reports pickers (Administrator + Manager):
 * a tinted icon square, title + description, and a selected treatment (accent
 * ring + check badge). Port of the prototype `ReportPickCard`/`MrReportCard`;
 * the accent is the theme's secondary (orange) ramp.
 */
export function ReportPickerCard({
  icon,
  title,
  description,
  selected,
  onSelect,
  ref,
}: ReportPickerCardProps) {
  const theme = useTheme();
  const accent = theme.palette.secondary.main;

  return (
    <ButtonBase
      ref={ref}
      onClick={onSelect}
      aria-pressed={selected}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        textAlign: 'left',
        p: '15px 16px',
        width: '100%',
        position: 'relative',
        borderRadius: '6px',
        bgcolor: selected ? alpha(accent, 0.04) : 'background.paper',
        border: '1px solid',
        borderColor: selected ? 'secondary.main' : 'divider',
        boxShadow: selected ? `0 0 0 1px ${accent}` : 'none',
        transition: 'border-color 120ms, box-shadow 120ms',
        '&:hover': { borderColor: 'secondary.main' },
      }}
    >
      {selected && (
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            width: 20,
            height: 20,
            borderRadius: '50%',
            bgcolor: 'secondary.main',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <Check sx={{ fontSize: 13, color: 'secondary.contrastText' }} />
        </Box>
      )}
      <Box
        sx={{
          display: 'grid',
          placeItems: 'center',
          width: 38,
          height: 38,
          flexShrink: 0,
          borderRadius: '8px',
          bgcolor: alpha(accent, 0.09),
          color: 'secondary.main',
        }}
      >
        {icon}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, minWidth: 0, pr: 2 }}>
        <Typography component="span" sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>
          {title}
        </Typography>
        <Typography
          component="span"
          sx={{ fontSize: 12.5, lineHeight: 1.45, color: 'text.secondary' }}
        >
          {description}
        </Typography>
      </Box>
    </ButtonBase>
  );
}
